import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";

// ── Tipler ──────────────────────────────────────────────────────────────────

type ParseResult = Awaited<ReturnType<typeof api.parsePerformanceCsv>>;
type IngestResult = Awaited<ReturnType<typeof api.ingestPerformanceCsv>>;
type CreativeRow = Awaited<ReturnType<typeof api.getPerformanceCreatives>>["creatives"][number];
type Benchmarks = Record<string, { target: number; direction: string; overridden: boolean }>;
type PerfSettings = { min_spend: number; min_impressions: number; primary_metric: string };

const MAPPING_FIELDS: { key: string; label: string; required?: boolean }[] = [
  { key: "ad_name", label: "Ad Name", required: true },
  { key: "spend", label: "Harcama (Amount Spent)" },
  { key: "impressions", label: "Impressions" },
  { key: "cpm", label: "CPM" },
  { key: "link_ctr", label: "Link CTR (%)" },
  { key: "outbound_clicks", label: "Outbound Clicks" },
  { key: "cost_per_outbound_click", label: "Cost / Outbound Click" },
  { key: "registrations", label: "Registrations" },
  { key: "cost_per_registration", label: "Cost / Registration" },
  { key: "purchases", label: "Purchases" },
  { key: "cost_per_purchase", label: "Cost / Purchase" },
];

const METRIC_COLS: { key: string; label: string; fmt: (v: number) => string }[] = [
  { key: "spend", label: "Spend", fmt: (v) => `$${v.toFixed(2)}` },
  { key: "impressions", label: "Impr.", fmt: (v) => v >= 1000 ? `${(v / 1000).toFixed(1)}K` : String(Math.round(v)) },
  { key: "cpm", label: "CPM", fmt: (v) => `$${v.toFixed(2)}` },
  { key: "link_ctr", label: "CTR", fmt: (v) => `${v.toFixed(2)}%` },
  { key: "cost_per_outbound_click", label: "CPC", fmt: (v) => `$${v.toFixed(2)}` },
  { key: "cost_per_registration", label: "CPR", fmt: (v) => `$${v.toFixed(2)}` },
  { key: "cost_per_purchase", label: "CPP", fmt: (v) => `$${v.toFixed(2)}` },
];

const BENCHMARK_LABELS: Record<string, string> = {
  cpm: "CPM ($)",
  link_ctr: "Link CTR (%)",
  cost_per_outbound_click: "Cost / Outbound Click ($)",
  cost_per_registration: "Cost / Registration ($)",
  cost_per_purchase: "Cost / Purchase ($)",
};

type Tab = "creatives" | "insights" | "brief" | "qa" | "portfolio" | "upload" | "uploads" | "benchmarks";

export function Performance({ productId }: { productId: string }) {
  const [tab, setTab] = useState<Tab>("creatives");

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-xl font-bold">📈 Performance</h1>
          <p className="text-sm text-zinc-500 mt-0.5">
            Reklam sonuçlarını CSV ile içeri al, kreatif bazında benchmark'larla karşılaştır.
          </p>
        </div>
      </div>

      <div className="flex gap-1 mb-5 border-b">
        {([
          ["creatives", "Kreatif Performansı"],
          ["insights", "🔍 Insights"],
          ["brief", "🧠 Brief"],
          ["qa", "🎬 QA"],
          ["portfolio", "📊 Portfolio"],
          ["upload", "📥 CSV Yükle"],
          ["uploads", "Yüklemeler"],
          ["benchmarks", "🎯 Benchmark'lar"],
        ] as [Tab, string][]).map(([key, label]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === key ? "border-accent text-accent" : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === "creatives" && <CreativesTab productId={productId} />}
      {tab === "insights" && <InsightsTab productId={productId} />}
      {tab === "brief" && <BriefTab productId={productId} />}
      {tab === "qa" && <QaTab productId={productId} />}
      {tab === "portfolio" && <PortfolioTab productId={productId} />}
      {tab === "upload" && <UploadTab onDone={() => setTab("creatives")} />}
      {tab === "uploads" && <UploadsTab />}
      {tab === "benchmarks" && <BenchmarksTab productId={productId} />}
    </div>
  );
}

// ── Kreatif Performansı ─────────────────────────────────────────────────────

function CreativesTab({ productId }: { productId: string }) {
  const [creatives, setCreatives] = useState<CreativeRow[]>([]);
  const [benchmarks, setBenchmarks] = useState<Benchmarks>({});
  const [settings, setSettings] = useState<PerfSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [backfilling, setBackfilling] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.getPerformanceCreatives(productId)
      .then((r) => { setCreatives(r.creatives); setBenchmarks(r.benchmarks); setSettings(r.settings); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [productId]);

  useEffect(load, [load]);

  async function runBackfill() {
    setBackfilling(true);
    try {
      const r = await api.backfillGenome();
      alert(`Genome backfill tamam: ${r.media_backfilled} görsel, ${r.ugc_backfilled} video kreatif işlendi.`);
      load();
    } catch { /* http() zaten toast atıyor */ }
    setBackfilling(false);
  }

  const filtered = creatives.filter((c) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return c.code.toLowerCase().includes(q)
      || (c.ad_name ?? "").toLowerCase().includes(q)
      || Object.values(c.variables).some((v) => v.toLowerCase().includes(q));
  });

  const winners = creatives.filter((c) => c.status === "winner").length;

  if (loading) return <div className="text-sm text-zinc-400 py-10 text-center">Yükleniyor…</div>;

  if (creatives.length === 0) {
    return (
      <div className="text-center py-14 text-zinc-400">
        <p className="text-sm">Bu ürün için henüz performans verisi yok.</p>
        <p className="text-xs mt-1">"CSV Yükle" sekmesinden ilk export'u içeri al.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="relative w-72">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 text-sm">🔍</span>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="KOD, ad name, variable…"
            className="w-full pl-9 pr-3 py-2 border border-zinc-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-accent/40"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs border rounded-full px-3 py-1 font-medium bg-emerald-50 text-emerald-700 border-emerald-200">
            🏆 {winners} winner
          </span>
          <span className="text-xs text-zinc-400">{creatives.length} kreatif</span>
          <button
            onClick={runBackfill}
            disabled={backfilling}
            title="Mevcut kreatiflerin variable'larını (feature, audience, senaryo…) geriye dönük doldurur"
            className="text-xs px-3 py-1.5 rounded border border-zinc-300 text-zinc-600 hover:border-accent hover:text-accent disabled:opacity-50"
          >
            {backfilling ? "Dolduruluyor…" : "🧬 Genome backfill"}
          </button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-200">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <Th>KOD</Th>
              <Th>DURUM</Th>
              {METRIC_COLS.map((m) => <Th key={m.key} center>{m.label}</Th>)}
              <Th>VARIABLE'LAR</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.code} className="border-b border-zinc-100 hover:bg-zinc-50 transition-colors align-top">
                <td className="px-3 py-2.5 font-mono text-xs text-zinc-700 whitespace-nowrap" title={c.ad_name ?? undefined}>
                  {c.code}
                </td>
                <td className="px-3 py-2.5 whitespace-nowrap"><StatusBadge status={c.status} settings={settings} /></td>
                {METRIC_COLS.map((m) => {
                  const val = c.metrics[m.key];
                  const ev = c.evaluation[m.key];
                  return (
                    <td key={m.key} className={`px-3 py-2.5 text-center text-xs font-mono whitespace-nowrap ${
                      ev === "pass" ? "text-emerald-600 font-semibold"
                      : ev === "fail" ? "text-red-500"
                      : "text-zinc-600"
                    }`}
                      title={benchmarks[m.key] ? `Benchmark: ${benchmarks[m.key]!.target}` : undefined}
                    >
                      {val == null ? "—" : m.fmt(val)}
                    </td>
                  );
                })}
                <td className="px-3 py-2.5">
                  <div className="flex flex-wrap gap-1 max-w-md">
                    {Object.entries(c.variables)
                      .filter(([k]) => k !== "creative_kind")
                      .map(([k, v]) => (
                        <span key={k} className="text-[10px] bg-zinc-100 text-zinc-600 rounded px-1.5 py-0.5" title={k}>
                          {v}
                        </span>
                      ))}
                    {Object.keys(c.variables).length === 0 && (
                      <span className="text-[10px] text-zinc-300">genome yok — backfill çalıştır</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="text-center py-8 text-sm text-zinc-400">Sonuç bulunamadı.</div>
        )}
      </div>
    </div>
  );
}

// ── Insights (variable bazlı lift analizi) ─────────────────────────────────

type InsightRow = Awaited<ReturnType<typeof api.getPerformanceInsights>>["insights"][number];

const INSIGHT_METRIC_LABELS: Record<string, string> = {
  cpm: "CPM",
  link_ctr: "CTR",
  cost_per_outbound_click: "CPC",
  cost_per_registration: "CPR",
  cost_per_purchase: "CPP",
};

function InsightsTab({ productId }: { productId: string }) {
  const [insights, setInsights] = useState<InsightRow[]>([]);
  const [note, setNote] = useState<string | null>(null);
  const [primaryMetric, setPrimaryMetric] = useState("cost_per_purchase");
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<string | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    setSummary(null);
    api.getPerformanceInsights(productId)
      .then((r) => { setInsights(r.insights); setNote(r.note ?? null); setPrimaryMetric(r.settings.primary_metric); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [productId]);

  async function generateSummary() {
    setSummaryLoading(true);
    try {
      const r = await api.getPerformanceInsightsSummary(productId);
      setSummary(r.summary);
    } catch { /* toast handled */ }
    setSummaryLoading(false);
  }

  if (loading) return <div className="text-sm text-zinc-400 py-10 text-center">Analiz ediliyor…</div>;

  if (insights.length === 0) {
    return (
      <div className="text-center py-14 text-zinc-400">
        <p className="text-sm">{note ?? "Henüz belirgin bir variable-performans ilişkisi bulunamadı."}</p>
        <p className="text-xs mt-1">Her grup için en az 3 değerlendirilebilir kreatif ve 3 karşılaştırma kreatifi gerekiyor.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <p className="text-xs text-zinc-500 max-w-lg">
          Her satır bir değişken değerinin (örn. <code className="bg-zinc-100 px-1 rounded">audience=seniors</code>) geri kalan kreatiflere göre performans farkını gösterir. Lift, birincil metrik olan <b>{INSIGHT_METRIC_LABELS[primaryMetric] ?? primaryMetric}</b> baz alınarak sıralanmıştır.
        </p>
        <button
          onClick={generateSummary}
          disabled={summaryLoading}
          className="text-xs px-3 py-1.5 rounded border border-zinc-300 text-zinc-600 hover:border-accent hover:text-accent disabled:opacity-50 whitespace-nowrap"
        >
          {summaryLoading ? "Yazılıyor…" : "🤖 AI özeti üret"}
        </button>
      </div>

      {summary && (
        <div className="mb-5 bg-accent/5 border border-accent/20 rounded-xl p-4">
          <p className="text-xs font-semibold text-accent mb-2">AI Özeti</p>
          <div className="text-sm text-zinc-700 whitespace-pre-wrap leading-relaxed">{summary}</div>
        </div>
      )}

      <div className="overflow-x-auto rounded-xl border border-zinc-200">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              <Th>VARIABLE</Th>
              <Th>DEĞER</Th>
              <Th center>KREATİF</Th>
              <Th center>SPEND</Th>
              {Object.entries(INSIGHT_METRIC_LABELS).map(([k, l]) => (
                <Th key={k} center>{l} lift{k === primaryMetric ? " ★" : ""}</Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {insights.map((row, i) => (
              <tr key={i} className="border-b border-zinc-100 hover:bg-zinc-50">
                <td className="px-3 py-2.5 text-xs text-zinc-500">{row.variable}</td>
                <td className="px-3 py-2.5 text-sm font-medium text-zinc-800">{row.value}</td>
                <td className="px-3 py-2.5 text-xs text-center font-mono">{row.creative_count}</td>
                <td className="px-3 py-2.5 text-xs text-center font-mono">${row.total_spend.toFixed(0)}</td>
                {Object.keys(INSIGHT_METRIC_LABELS).map((metric) => {
                  const lift = row.lift_pct[metric];
                  return (
                    <td key={metric} className={`px-3 py-2.5 text-xs text-center font-mono font-semibold ${
                      lift == null ? "text-zinc-300"
                      : lift > 0 ? "text-emerald-600"
                      : lift < 0 ? "text-red-500"
                      : "text-zinc-400"
                    }`}>
                      {lift == null ? "—" : `${lift > 0 ? "+" : ""}${lift.toFixed(1)}%`}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Brief Generator (Faz 4) ─────────────────────────────────────────────────
// Approach B: core message'dan yeni pain point öner. Approach A: kazanan Custom
// senaryonun seçilen alt kırılımlarını değiştirerek varyasyon üret.

const SCENARIO_VARY_OPTIONS: { key: string; label: string }[] = [
  { key: "location", label: "Location" },
  { key: "environment", label: "Environment" },
  { key: "music", label: "Music" },
  { key: "cam_angle", label: "Cam Angle" },
  { key: "cam_movement", label: "Cam Movement" },
  { key: "emotion", label: "Emotion" },
  { key: "role_archetype", label: "Role Archetype" },
  { key: "season", label: "Season" },
];

function BriefTab({ productId }: { productId: string }) {
  const [mode, setMode] = useState<"duplicate" | "variation" | "new_hook">("duplicate");

  return (
    <div className="max-w-3xl">
      <div className="flex gap-2 mb-5">
        <button
          onClick={() => setMode("duplicate")}
          className={`flex-1 text-left border rounded-xl p-4 transition-colors ${
            mode === "duplicate" ? "border-accent bg-accent/5" : "border-zinc-200 bg-white hover:border-zinc-300"
          }`}
        >
          <p className="font-semibold text-sm">Kazananı Çoğalt (L1-L3, Görsel)</p>
          <p className="text-xs text-zinc-500 mt-1">Bir winner image kodunu tek tıkla resize / CTA-hook varyasyonu / sahne değişimi ile çoğalt.</p>
        </button>
        <button
          onClick={() => setMode("variation")}
          className={`flex-1 text-left border rounded-xl p-4 transition-colors ${
            mode === "variation" ? "border-accent bg-accent/5" : "border-zinc-200 bg-white hover:border-zinc-300"
          }`}
        >
          <p className="font-semibold text-sm">Approach A — Kazanan Konseptte Varyasyon (Video)</p>
          <p className="text-xs text-zinc-500 mt-1">Bir UGC senaryosunu sabit tutup seçtiğin değişkenleri (location, cam angle…) farklılaştır.</p>
        </button>
        <button
          onClick={() => setMode("new_hook")}
          className={`flex-1 text-left border rounded-xl p-4 transition-colors ${
            mode === "new_hook" ? "border-accent bg-accent/5" : "border-zinc-200 bg-white hover:border-zinc-300"
          }`}
        >
          <p className="font-semibold text-sm">Approach B — Yeni Hook Geliştir</p>
          <p className="text-xs text-zinc-500 mt-1">Core message'dan sıfırdan yeni pain point öner.</p>
        </button>
      </div>

      {mode === "duplicate" && <DuplicateWinnerBrief productId={productId} />}
      {mode === "variation" && <VariationBrief productId={productId} />}
      {mode === "new_hook" && <NewHookBrief productId={productId} />}
    </div>
  );
}

function DuplicateWinnerBrief({ productId }: { productId: string }) {
  const [code, setCode] = useState("");
  const [level, setLevel] = useState<"L1_resize" | "L2_element_swap" | "L3_scene_change">("L1_resize");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ edit_job_id: string; operation: string } | null>(null);

  const LEVELS: { key: typeof level; label: string; desc: string }[] = [
    { key: "L1_resize", label: "L1 · Resize/Reformat", desc: "Aynı kreatif, eksik en-boy oranlarına (9:16/1:1/4:5/16:9) uyarlanır." },
    { key: "L2_element_swap", label: "L2 · Element Swap", desc: "Hook ve CTA'nın farklı varyasyonlarını üretir, görsel aynı kalır." },
    { key: "L3_scene_change", label: "L3 · Scene Change", desc: "Görseli yeniden yaratır (image→text→image) — aynı konsept, yeni sahne." },
  ];

  async function run() {
    if (!code.trim()) return;
    setBusy(true);
    setResult(null);
    try {
      const r = await api.duplicateWinner(productId, code.trim(), level);
      if (r.ok) setResult({ edit_job_id: r.edit_job_id!, operation: r.operation! });
    } catch { /* toast handled */ }
    setBusy(false);
  }

  return (
    <div className="bg-white border rounded-xl p-5">
      <label className="text-sm block mb-3">
        <span className="text-zinc-600 block mb-1">Winner Kreatif Kodu</span>
        <input
          value={code}
          onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="Örn: BIQ_00139"
          className="w-full border rounded px-3 py-2 text-sm font-mono"
        />
        <span className="text-xs text-zinc-400 mt-1 block">Kreatif Performansı sekmesindeki 🏆 Winner kodlarından birini gir.</span>
      </label>

      <p className="text-xs text-zinc-500 mb-2">Seviye seç:</p>
      <div className="space-y-2 mb-4">
        {LEVELS.map((l) => (
          <button
            key={l.key}
            onClick={() => setLevel(l.key)}
            className={`w-full text-left border rounded-lg p-3 transition-colors ${
              level === l.key ? "border-accent bg-accent/5" : "border-zinc-200 hover:border-zinc-300"
            }`}
          >
            <p className="text-sm font-medium">{l.label}</p>
            <p className="text-xs text-zinc-500 mt-0.5">{l.desc}</p>
          </button>
        ))}
      </div>

      <button
        onClick={run}
        disabled={!code.trim() || busy}
        className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-semibold disabled:opacity-50"
      >
        {busy ? "Başlatılıyor…" : "🔁 Çoğalt"}
      </button>

      {result && (
        <p className="text-xs text-emerald-600 mt-3">
          ✓ Edit job kuyruğa alındı ({result.operation}) — Gallery/Queue'dan takip edebilirsin.
        </p>
      )}
    </div>
  );
}

function NewHookBrief({ productId }: { productId: string }) {
  const [features, setFeatures] = useState<{ id: string; name: string; pain_points: string[] }[]>([]);
  const [featureId, setFeatureId] = useState("");
  const [count, setCount] = useState(3);
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<{ pain_point: string; rationale: string }[]>([]);
  const [added, setAdded] = useState<Set<number>>(new Set());

  useEffect(() => {
    api.listFeatures(productId).then((r) => setFeatures(r.features ?? [])).catch(() => {});
  }, [productId]);

  async function generate() {
    if (!featureId) return;
    setLoading(true);
    setSuggestions([]);
    setAdded(new Set());
    try {
      const r = await api.generateNewHooks(productId, featureId, count);
      setSuggestions(r.suggestions ?? []);
    } catch { /* toast handled */ }
    setLoading(false);
  }

  async function addToFeature(idx: number) {
    const feature = features.find((f) => f.id === featureId);
    const suggestion = suggestions[idx];
    if (!feature || !suggestion) return;
    await api.updateFeature(productId, featureId, { pain_points: [...feature.pain_points, suggestion.pain_point] });
    setFeatures((prev) => prev.map((f) => f.id === featureId ? { ...f, pain_points: [...f.pain_points, suggestion.pain_point] } : f));
    setAdded((prev) => new Set(prev).add(idx));
  }

  return (
    <div>
      <div className="bg-white border rounded-xl p-5 mb-4">
        <div className="flex items-end gap-3">
          <label className="flex-1 text-sm">
            <span className="text-zinc-600 block mb-1">Feature</span>
            <select value={featureId} onChange={(e) => setFeatureId(e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
              <option value="">— seç —</option>
              {features.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </label>
          <label className="text-sm">
            <span className="text-zinc-600 block mb-1">Adet</span>
            <select value={count} onChange={(e) => setCount(Number(e.target.value))} className="border rounded px-3 py-2 text-sm">
              {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button
            onClick={generate}
            disabled={!featureId || loading}
            className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            {loading ? "Üretiliyor…" : "🤖 Öner"}
          </button>
        </div>
      </div>

      {suggestions.length > 0 && (
        <div className="space-y-2">
          {suggestions.map((s, i) => (
            <div key={i} className="bg-white border rounded-xl p-4 flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-zinc-800">{s.pain_point}</p>
                <p className="text-xs text-zinc-500 mt-1">{s.rationale}</p>
              </div>
              <button
                onClick={() => addToFeature(i)}
                disabled={added.has(i)}
                className="shrink-0 text-xs px-3 py-1.5 rounded border border-zinc-300 text-zinc-600 hover:border-accent hover:text-accent disabled:opacity-50 disabled:hover:border-zinc-300"
              >
                {added.has(i) ? "✓ Eklendi" : "+ Pain Point olarak ekle"}
              </button>
            </div>
          ))}
          <p className="text-xs text-zinc-400 pt-1">
            Eklenen pain point'ler Ürün Ayarları → Feature Listesi'nde görünür. Üretimi başlatmak için Image Creation formundan seçebilirsin.
          </p>
        </div>
      )}
    </div>
  );
}

function VariationBrief({ productId }: { productId: string }) {
  type Scenario = { id: string; name: string; description: string };
  const [scenarios, setScenarios] = useState<Scenario[]>([]);
  const [scenarioId, setScenarioId] = useState("");
  const [vary, setVary] = useState<Set<string>>(new Set(["location", "cam_angle"]));
  const [count, setCount] = useState(3);
  const [loading, setLoading] = useState(false);
  const [variations, setVariations] = useState<{ name: string; variables: Record<string, string>; description_update_note: string }[]>([]);
  const [saved, setSaved] = useState<Set<number>>(new Set());

  useEffect(() => {
    api.listUGCScenarios(productId).then((r) => setScenarios(r.ugc_scenarios ?? [])).catch(() => {});
  }, [productId]);

  function toggleVary(key: string) {
    setVary((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function generate() {
    if (!scenarioId || vary.size === 0) return;
    setLoading(true);
    setVariations([]);
    setSaved(new Set());
    try {
      const r = await api.varyScenario(productId, scenarioId, [...vary], count);
      setVariations(r.variations ?? []);
    } catch { /* toast handled */ }
    setLoading(false);
  }

  async function save(idx: number) {
    const v = variations[idx];
    if (!v) return;
    await api.saveScenarioVariation(productId, scenarioId, v.name, v.variables);
    setSaved((prev) => new Set(prev).add(idx));
  }

  return (
    <div>
      <div className="bg-white border rounded-xl p-5 mb-4">
        <label className="text-sm block mb-3">
          <span className="text-zinc-600 block mb-1">Kaynak Senaryo (Custom)</span>
          <select value={scenarioId} onChange={(e) => setScenarioId(e.target.value)} className="w-full border rounded px-3 py-2 text-sm">
            <option value="">— seç —</option>
            {scenarios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>

        <p className="text-xs text-zinc-500 mb-2">Değişecek değişkenler (seçmediklerin sabit kalır):</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {SCENARIO_VARY_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              onClick={() => toggleVary(opt.key)}
              className={`text-xs px-3 py-1.5 rounded-full border font-medium ${
                vary.has(opt.key) ? "bg-accent text-white border-accent" : "bg-white text-zinc-500 border-zinc-300"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className="flex items-end gap-3">
          <label className="text-sm">
            <span className="text-zinc-600 block mb-1">Adet</span>
            <select value={count} onChange={(e) => setCount(Number(e.target.value))} className="border rounded px-3 py-2 text-sm">
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
          <button
            onClick={generate}
            disabled={!scenarioId || vary.size === 0 || loading}
            className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            {loading ? "Üretiliyor…" : "🤖 Varyasyon Üret"}
          </button>
        </div>
      </div>

      {variations.length > 0 && (
        <div className="space-y-2">
          {variations.map((v, i) => (
            <div key={i} className="bg-white border rounded-xl p-4">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-zinc-800">{v.name}</p>
                <button
                  onClick={() => save(i)}
                  disabled={saved.has(i)}
                  className="shrink-0 text-xs px-3 py-1.5 rounded border border-zinc-300 text-zinc-600 hover:border-accent hover:text-accent disabled:opacity-50 disabled:hover:border-zinc-300"
                >
                  {saved.has(i) ? "✓ Kaydedildi" : "💾 Senaryo olarak kaydet"}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {Object.entries(v.variables).map(([k, val]) => (
                  <span key={k} className="text-[10px] bg-zinc-100 text-zinc-600 rounded px-1.5 py-0.5">{k}: {val}</span>
                ))}
              </div>
              <p className="text-xs text-zinc-500 mt-2">{v.description_update_note}</p>
            </div>
          ))}
          <p className="text-xs text-zinc-400 pt-1">
            Kaydedilen senaryolar Ürün Ayarları → UGC Senaryoları'nda görünür. Üretim için Scenario formundan seçebilirsin.
          </p>
        </div>
      )}
    </div>
  );
}

// ── Video QA (Faz 5) ────────────────────────────────────────────────────────
// Üretim anında Gemini 2.5 tarafından otomatik değerlendirilen videolar —
// yayına çıkmadan önce gözden geçirme kuyruğu.

type QaRow = Awaited<ReturnType<typeof api.getQaResults>>["results"][number];

function QaTab({ productId }: { productId: string }) {
  const [results, setResults] = useState<QaRow[]>([]);
  const [failCount, setFailCount] = useState(0);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getQaResults(productId, onlyFailed)
      .then((r) => { setResults(r.results); setFailCount(r.fail_count); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [productId, onlyFailed]);

  if (loading) return <div className="text-sm text-zinc-400 py-10 text-center">Yükleniyor…</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-zinc-500 max-w-lg">
          Her video üretimi tamamlandığında Gemini 2.5 script/prompt uygunluğu, lip-sync ve görsel artifact açısından otomatik değerlendirilir. Sonuç üretimi ASLA bloklamaz — sadece burada gözden geçirmen için işaretlenir.
        </p>
        <div className="flex items-center gap-3">
          {failCount > 0 && (
            <span className="text-xs border rounded-full px-3 py-1 font-medium bg-red-50 text-red-600 border-red-200">
              ⚠️ {failCount} sorunlu
            </span>
          )}
          <label className="flex items-center gap-1.5 text-xs text-zinc-500">
            <input type="checkbox" checked={onlyFailed} onChange={(e) => setOnlyFailed(e.target.checked)} />
            sadece sorunlular
          </label>
        </div>
      </div>

      {results.length === 0 ? (
        <div className="text-center py-14 text-zinc-400">
          <p className="text-sm">{onlyFailed ? "Sorunlu video yok 🎉" : "Henüz QA sonucu yok — video üretimleri tamamlandıkça burada görünür."}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {results.map((r) => (
            <div key={r.id} className={`bg-white border rounded-xl p-4 ${r.pass ? "border-zinc-200" : "border-red-200 bg-red-50/30"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs text-zinc-500">{r.code}</span>
                  <span className="text-[10px] bg-zinc-100 text-zinc-600 rounded px-1.5 py-0.5 uppercase">{r.kind}</span>
                  {r.pass
                    ? <span className="text-[11px] font-semibold border rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">✅ Geçti ({r.score})</span>
                    : <span className="text-[11px] font-semibold border rounded-full px-2 py-0.5 bg-red-50 text-red-600 border-red-200">⚠️ Sorunlu ({r.score})</span>}
                </div>
                <span className="text-[10px] text-zinc-400 whitespace-nowrap">{r.checked_at?.slice(0, 16)}</span>
              </div>
              <p className="text-sm text-zinc-700 mt-2">{r.summary}</p>
              {r.issues.length > 0 && (
                <ul className="mt-2 space-y-0.5">
                  {r.issues.map((issue, i) => (
                    <li key={i} className="text-xs text-red-600">• {issue}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Portfolio Thinking (Faz 5.5, madde 7) ───────────────────────────────────
// "Optimize the collection, not individuals" — değişken bazında çeşitlilik/
// konsantrasyon skoru. Yüksek konsantrasyon = aynı archetype/emotion'a aşırı
// yatırım, düşük çeşitlilik riski.

type PortfolioRow = Awaited<ReturnType<typeof api.getPortfolio>>["portfolio"][number];

function PortfolioTab({ productId }: { productId: string }) {
  const [rows, setRows] = useState<PortfolioRow[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.getPortfolio(productId).then((r) => setRows(r.portfolio)).catch(() => {}).finally(() => setLoading(false));
  }, [productId]);

  if (loading) return <div className="text-sm text-zinc-400 py-10 text-center">Yükleniyor…</div>;
  if (rows.length === 0) return <div className="text-sm text-zinc-400 py-10 text-center">Henüz genome verisi yok.</div>;

  return (
    <div>
      <p className="text-xs text-zinc-500 max-w-lg mb-4">
        Her değişken için koleksiyonun ne kadar çeşitli olduğunu gösterir (Herfindahl konsantrasyon indeksi, 0 = tam çeşitli, 1 = tek değere yığılmış). ⚠️ işaretli satırlar en az 5 kreatifte %50'den fazla tek bir değere yığılmış — çeşitlilik riski.
      </p>
      <div className="space-y-3">
        {rows.map((row) => (
          <div key={row.variable} className={`bg-white border rounded-xl p-4 ${row.flagged ? "border-amber-300 bg-amber-50/30" : "border-zinc-200"}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold">{row.variable}</span>
                {row.flagged && <span className="text-[10px] bg-amber-100 text-amber-700 rounded px-1.5 py-0.5">⚠️ konsantre</span>}
              </div>
              <span className="text-xs text-zinc-400">{row.total_creatives} kreatif · {row.distinct_values} farklı değer · konsantrasyon {row.concentration}</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {row.top_values.map((v) => (
                <span key={v.value} className="text-xs bg-zinc-100 text-zinc-600 rounded px-2 py-0.5">
                  {v.value} <span className="text-zinc-400">({v.share}%)</span>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status, settings }: { status: CreativeRow["status"]; settings: PerfSettings | null }) {
  switch (status) {
    case "winner":
      return <span className="text-[11px] font-semibold border rounded-full px-2 py-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">🏆 Winner</span>;
    case "loser":
      return <span className="text-[11px] font-medium border rounded-full px-2 py-0.5 bg-red-50 text-red-600 border-red-200">Benchmark altı</span>;
    case "insufficient":
      return (
        <span
          className="text-[11px] border rounded-full px-2 py-0.5 bg-zinc-50 text-zinc-400 border-zinc-200"
          title={settings ? `Değerlendirme eşiği: ≥$${settings.min_spend} spend veya ≥${settings.min_impressions} impression` : undefined}
        >
          Veri az
        </span>
      );
    default:
      return <span className="text-[11px] border rounded-full px-2 py-0.5 bg-zinc-50 text-zinc-400 border-zinc-200">—</span>;
  }
}

// ── CSV Yükleme ─────────────────────────────────────────────────────────────

function UploadTab({ onDone }: { onDone: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [csvText, setCsvText] = useState<string | null>(null);
  const [filename, setFilename] = useState("");
  const [platform, setPlatform] = useState("meta");
  const [parsed, setParsed] = useState<ParseResult | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [periodStart, setPeriodStart] = useState("");
  const [periodEnd, setPeriodEnd] = useState("");
  const [savePreset, setSavePreset] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<IngestResult | null>(null);
  const [showUnmatched, setShowUnmatched] = useState(false);

  async function handleFile(file: File) {
    const text = await file.text();
    setCsvText(text);
    setFilename(file.name);
    setResult(null);
    setBusy(true);
    try {
      const r = await api.parsePerformanceCsv(text, platform);
      setParsed(r);
      setMapping(r.suggested_mapping);
    } catch { setParsed(null); }
    setBusy(false);
  }

  async function ingest() {
    if (!csvText || !mapping.ad_name) return;
    setBusy(true);
    try {
      const r = await api.ingestPerformanceCsv({
        csv: csvText,
        filename,
        platform,
        mapping,
        period_start: periodStart || undefined,
        period_end: periodEnd || undefined,
        save_preset: savePreset,
      });
      setResult(r);
    } catch { /* toast handled */ }
    setBusy(false);
  }

  if (result) {
    const matchRate = result.row_count > 0 ? Math.round((result.matched_count / result.row_count) * 100) : 0;
    return (
      <div className="max-w-2xl">
        <div className="bg-white border rounded-xl p-6">
          <h3 className="font-bold text-lg mb-1">✅ Yükleme tamamlandı</h3>
          <p className="text-sm text-zinc-600 mb-4">
            {result.row_count} satırdan <b>{result.matched_count}</b> tanesi kreatif koduyla eşleşti (%{matchRate}).
          </p>
          {result.unmatched.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setShowUnmatched(!showUnmatched)}
                className="text-sm text-amber-600 font-medium hover:underline"
              >
                ⚠️ {result.unmatched.length} satır eşleşmedi {showUnmatched ? "▲" : "▼"}
              </button>
              {showUnmatched && (
                <div className="mt-2 max-h-48 overflow-y-auto bg-amber-50 border border-amber-200 rounded p-3">
                  {result.unmatched.map((name, i) => (
                    <div key={i} className="text-xs font-mono text-amber-800 py-0.5">{name}</div>
                  ))}
                </div>
              )}
              <p className="text-xs text-zinc-400 mt-1">
                Ad name içinde ürün tag'i + kod (örn. BIQ_00139) bulunamayan satırlar atlandı.
              </p>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={onDone} className="px-4 py-2 bg-accent text-white rounded-lg text-sm font-medium">
              Kreatif performansına git →
            </button>
            <button
              onClick={() => { setResult(null); setParsed(null); setCsvText(null); }}
              className="px-4 py-2 border rounded-lg text-sm text-zinc-600"
            >
              Yeni CSV yükle
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      {/* Adım 1: dosya seç */}
      <div
        className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          csvText ? "border-emerald-300 bg-emerald-50/50" : "border-zinc-300 bg-white hover:border-accent"
        }`}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files[0]; if (f) handleFile(f); }}
      >
        <input
          ref={fileRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
        />
        {csvText ? (
          <div className="text-sm">
            <span className="font-medium text-emerald-700">📄 {filename}</span>
            {parsed && <span className="text-zinc-500"> — {parsed.row_count} satır, {parsed.headers.length} kolon</span>}
            <button onClick={() => fileRef.current?.click()} className="ml-3 text-xs text-accent hover:underline">değiştir</button>
          </div>
        ) : (
          <button onClick={() => fileRef.current?.click()} className="text-sm text-zinc-500">
            <span className="text-3xl block mb-2">📥</span>
            Meta/TikTok/Google export CSV'sini buraya sürükle ya da <span className="text-accent font-medium">seç</span>
          </button>
        )}
        <div className="mt-3 flex justify-center gap-2">
          {["meta", "tiktok", "google"].map((p) => (
            <button
              key={p}
              onClick={() => setPlatform(p)}
              className={`text-xs px-3 py-1 rounded-full border font-medium capitalize ${
                platform === p ? "bg-accent text-white border-accent" : "bg-white text-zinc-500 border-zinc-300"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {busy && !parsed && <div className="text-sm text-zinc-400 py-6 text-center">CSV inceleniyor…</div>}

      {/* Adım 2: kolon eşleme */}
      {parsed && (
        <div className="mt-5 bg-white border rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold">Kolon Eşleme</h3>
            {parsed.mapping_from_preset && (
              <span className="text-xs bg-blue-50 text-blue-600 border border-blue-200 rounded-full px-2 py-0.5">
                kayıtlı şablondan yüklendi
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-6 gap-y-2 mb-4">
            {MAPPING_FIELDS.map((f) => (
              <label key={f.key} className="flex items-center justify-between gap-2 text-sm">
                <span className={f.required ? "font-medium" : "text-zinc-600"}>
                  {f.label}{f.required && <span className="text-red-500"> *</span>}
                </span>
                <select
                  value={mapping[f.key] ?? ""}
                  onChange={(e) => setMapping((m) => {
                    const next = { ...m };
                    if (e.target.value) next[f.key] = e.target.value;
                    else delete next[f.key];
                    return next;
                  })}
                  className="border rounded px-2 py-1 text-xs max-w-[220px] bg-white"
                >
                  <option value="">— eşleme yok —</option>
                  {parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                </select>
              </label>
            ))}
          </div>

          <div className="flex items-center gap-4 mb-4 text-sm">
            <label className="flex items-center gap-2">
              <span className="text-zinc-600">Dönem:</span>
              <input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)}
                className="border rounded px-2 py-1 text-xs" />
              <span className="text-zinc-400">→</span>
              <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
                className="border rounded px-2 py-1 text-xs" />
            </label>
            <label className="flex items-center gap-1.5 text-xs text-zinc-500">
              <input type="checkbox" checked={savePreset} onChange={(e) => setSavePreset(e.target.checked)} />
              eşlemeyi şablon olarak kaydet
            </label>
          </div>

          {/* Eşleşme önizlemesi */}
          {mapping.ad_name && (
            <div className="mb-4 text-sm">
              <span className={parsed.match_preview.matched > 0 ? "text-emerald-600" : "text-red-500"}>
                {parsed.match_preview.matched} / {parsed.row_count} satır kreatif koduyla eşleşiyor
              </span>
              {parsed.match_preview.unmatched_sample.length > 0 && (
                <details className="mt-1">
                  <summary className="text-xs text-zinc-400 cursor-pointer">eşleşmeyen örnekler</summary>
                  {parsed.match_preview.unmatched_sample.map((s, i) => (
                    <div key={i} className="text-xs font-mono text-zinc-500 pl-3 py-0.5">{s}</div>
                  ))}
                </details>
              )}
            </div>
          )}

          {/* Önizleme tablosu */}
          <div className="overflow-x-auto border rounded-lg mb-4">
            <table className="text-xs border-collapse w-full">
              <thead>
                <tr className="bg-zinc-50">
                  {parsed.headers.slice(0, 8).map((h) => (
                    <th key={h} className={`px-2 py-1.5 text-left whitespace-nowrap font-medium ${
                      Object.values(mapping).includes(h) ? "text-accent" : "text-zinc-400"
                    }`}>{h}</th>
                  ))}
                  {parsed.headers.length > 8 && <th className="px-2 py-1.5 text-zinc-300">+{parsed.headers.length - 8}</th>}
                </tr>
              </thead>
              <tbody>
                {parsed.preview.map((row, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    {parsed.headers.slice(0, 8).map((h) => (
                      <td key={h} className="px-2 py-1 whitespace-nowrap max-w-[200px] truncate text-zinc-600">{row[h]}</td>
                    ))}
                    {parsed.headers.length > 8 && <td className="px-2 py-1 text-zinc-300">…</td>}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            onClick={ingest}
            disabled={busy || !mapping.ad_name || parsed.match_preview.matched === 0}
            className="px-5 py-2.5 bg-accent text-white rounded-lg text-sm font-semibold disabled:opacity-50"
          >
            {busy ? "İçeri alınıyor…" : `📥 ${parsed.match_preview.matched} satırı içeri al`}
          </button>
          {!mapping.ad_name && <span className="ml-3 text-xs text-red-500">Ad Name kolonu eşlenmeli</span>}
        </div>
      )}
    </div>
  );
}

// ── Yüklemeler ──────────────────────────────────────────────────────────────

function UploadsTab() {
  const [uploads, setUploads] = useState<Awaited<ReturnType<typeof api.listPerformanceUploads>>["uploads"]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.listPerformanceUploads().then((r) => setUploads(r.uploads)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(load, [load]);

  async function remove(id: string) {
    if (!confirm("Bu yükleme ve tüm performans satırları silinecek. Emin misin?")) return;
    await api.deletePerformanceUpload(id);
    load();
  }

  if (loading) return <div className="text-sm text-zinc-400 py-10 text-center">Yükleniyor…</div>;
  if (uploads.length === 0) return <div className="text-sm text-zinc-400 py-10 text-center">Henüz CSV yüklenmedi.</div>;

  return (
    <div className="overflow-x-auto rounded-xl border border-zinc-200 max-w-4xl">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-zinc-50 border-b border-zinc-200">
            <Th>DOSYA</Th>
            <Th>PLATFORM</Th>
            <Th>DÖNEM</Th>
            <Th center>SATIR</Th>
            <Th center>EŞLEŞEN</Th>
            <Th>TARİH</Th>
            <Th center></Th>
          </tr>
        </thead>
        <tbody>
          {uploads.map((u) => (
            <tr key={u.id} className="border-b border-zinc-100 hover:bg-zinc-50">
              <td className="px-4 py-2.5 text-xs font-medium text-zinc-700">{u.filename}</td>
              <td className="px-4 py-2.5 text-xs capitalize text-zinc-500">{u.platform}</td>
              <td className="px-4 py-2.5 text-xs text-zinc-500 whitespace-nowrap">
                {u.period_start || u.period_end ? `${u.period_start ?? "?"} → ${u.period_end ?? "?"}` : "—"}
              </td>
              <td className="px-4 py-2.5 text-xs text-center font-mono">{u.row_count}</td>
              <td className="px-4 py-2.5 text-xs text-center font-mono">
                <span className={u.matched_count < u.row_count ? "text-amber-600" : "text-emerald-600"}>
                  {u.matched_count}
                </span>
              </td>
              <td className="px-4 py-2.5 text-xs text-zinc-400 whitespace-nowrap">{u.created_at?.slice(0, 16)}</td>
              <td className="px-4 py-2.5 text-center">
                <button onClick={() => remove(u.id)} className="text-xs text-red-400 hover:text-red-600" title="Yüklemeyi geri al">
                  🗑
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Benchmark'lar ───────────────────────────────────────────────────────────

function BenchmarksTab({ productId }: { productId: string }) {
  const [benchmarks, setBenchmarks] = useState<Benchmarks>({});
  const [defaults, setDefaults] = useState<Record<string, number>>({});
  const [settings, setSettings] = useState<PerfSettings | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [settingDrafts, setSettingDrafts] = useState<{ min_spend: string; min_impressions: string }>({ min_spend: "", min_impressions: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => {
    setLoading(true);
    api.getBenchmarks(productId).then((r) => {
      setBenchmarks(r.benchmarks);
      setDefaults(r.defaults);
      setSettings(r.settings);
      setDrafts(Object.fromEntries(Object.entries(r.benchmarks).map(([k, v]) => [k, String(v.target)])));
      setSettingDrafts({ min_spend: String(r.settings.min_spend), min_impressions: String(r.settings.min_impressions) });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [productId]);

  async function save() {
    setSaving(true);
    const bm: Record<string, number | null> = {};
    for (const [metric, draft] of Object.entries(drafts)) {
      const num = Number(draft);
      if (!draft.trim() || !Number.isFinite(num)) continue;
      // Default'a eşitse override'ı kaldır
      bm[metric] = num === defaults[metric] ? null : num;
    }
    try {
      await api.putBenchmarks(productId, bm, {
        min_spend: Number(settingDrafts.min_spend) || undefined,
        min_impressions: Number(settingDrafts.min_impressions) || undefined,
      });
      setSavedAt(Date.now());
    } catch { /* toast handled */ }
    setSaving(false);
  }

  if (loading || !settings) return <div className="text-sm text-zinc-400 py-10 text-center">Yükleniyor…</div>;

  return (
    <div className="max-w-xl">
      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-bold mb-1">Hedef Değerler</h3>
        <p className="text-xs text-zinc-500 mb-4">
          Kreatifler bu hedeflerle karşılaştırılır. Birincil metrik (<b>Cost / Purchase</b>) hedefi geçen kreatif 🏆 Winner sayılır.
        </p>
        <div className="space-y-2">
          {Object.entries(benchmarks).map(([metric, bm]) => (
            <label key={metric} className="flex items-center justify-between gap-3 text-sm">
              <span className="text-zinc-700">
                {BENCHMARK_LABELS[metric] ?? metric}
                <span className="text-[10px] text-zinc-400 ml-1.5">{bm.direction === "below" ? "↓ düşük iyi" : "↑ yüksek iyi"}</span>
                {metric === settings.primary_metric && (
                  <span className="text-[10px] bg-accent/10 text-accent rounded px-1.5 py-0.5 ml-1.5 font-medium">birincil</span>
                )}
              </span>
              <div className="flex items-center gap-2">
                <input
                  value={drafts[metric] ?? ""}
                  onChange={(e) => setDrafts((d) => ({ ...d, [metric]: e.target.value }))}
                  className="w-24 border rounded px-2 py-1.5 text-sm font-mono text-right"
                />
                {bm.overridden && <span className="text-[10px] text-amber-500" title={`Default: ${defaults[metric]}`}>özel</span>}
              </div>
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded-xl p-5 mb-4">
        <h3 className="font-bold mb-1">Değerlendirme Eşiği</h3>
        <p className="text-xs text-zinc-500 mb-4">
          Bu eşiğin altındaki kreatifler "veri az" sayılır, winner/loser değerlendirmesine girmez.
        </p>
        <div className="flex gap-6 text-sm">
          <label className="flex items-center gap-2">
            <span className="text-zinc-600">Min. spend ($)</span>
            <input
              value={settingDrafts.min_spend}
              onChange={(e) => setSettingDrafts((s) => ({ ...s, min_spend: e.target.value }))}
              className="w-20 border rounded px-2 py-1.5 text-sm font-mono text-right"
            />
          </label>
          <label className="flex items-center gap-2">
            <span className="text-zinc-600">veya min. impression</span>
            <input
              value={settingDrafts.min_impressions}
              onChange={(e) => setSettingDrafts((s) => ({ ...s, min_impressions: e.target.value }))}
              className="w-24 border rounded px-2 py-1.5 text-sm font-mono text-right"
            />
          </label>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-5 py-2.5 bg-accent text-white rounded-lg text-sm font-semibold disabled:opacity-50"
        >
          {saving ? "Kaydediliyor…" : "Kaydet"}
        </button>
        {savedAt && Date.now() - savedAt < 4000 && <span className="text-sm text-emerald-600">✓ Kaydedildi</span>}
      </div>
    </div>
  );
}

function Th({ children, center }: { children?: React.ReactNode; center?: boolean }) {
  return (
    <th className={`px-3 py-2.5 text-xs font-semibold text-zinc-500 uppercase tracking-wide whitespace-nowrap ${center ? "text-center" : "text-left"}`}>
      {children}
    </th>
  );
}

// Idea Tree artık bağımsız bir sayfa: pages/CreativeTree.tsx
